import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import ToolCard from '../components/ToolCard';
import { 
  LayoutDashboard, Bookmark, BarChart3, Crown, 
  Zap, TrendingUp, ArrowRight 
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [savedTools, setSavedTools] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        const [statsRes, savedRes] = await Promise.all([
          axios.get(`${API}/user/stats`, { withCredentials: true }),
          axios.get(`${API}/user/saved-tools`, { withCredentials: true }),
        ]);
        setStats(statsRes.data);
        setSavedTools(savedRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const subscriptionBadge = {
    free: { label: 'Free', class: 'bg-muted text-muted-foreground' },
    pro: { label: 'Pro', class: 'bg-primary/20 text-primary' },
    business: { label: 'Business', class: 'bg-accent/20 text-accent' },
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full" />
            ) : (
              <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center text-xl font-bold">
                {user.name?.[0]}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']" data-testid="user-name">
                Welcome back, {user.name?.split(' ')[0]}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={subscriptionBadge[user.subscription]?.class}>
                  <Crown className="w-3 h-3 mr-1" />
                  {subscriptionBadge[user.subscription]?.label} Plan
                </Badge>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue={activeTab} className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex items-center gap-2" data-testid="tab-saved">
              <Bookmark className="w-4 h-4" />
              Saved Tools
            </TabsTrigger>
            <TabsTrigger value="subscription" className="flex items-center gap-2" data-testid="tab-subscription">
              <Crown className="w-4 h-4" />
              Subscription
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="stat-card p-6 rounded-xl border border-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <div className="text-3xl font-bold" data-testid="stat-usage">{stats?.usage_count || 0}</div>
                <div className="text-sm text-muted-foreground">Tools Used</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="stat-card p-6 rounded-xl border border-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <Bookmark className="w-5 h-5 text-accent" />
                </div>
                <div className="text-3xl font-bold" data-testid="stat-saved">{stats?.saved_tools_count || 0}</div>
                <div className="text-sm text-muted-foreground">Saved Tools</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="stat-card p-6 rounded-xl border border-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-3xl font-bold capitalize" data-testid="stat-plan">{stats?.subscription}</div>
                <div className="text-sm text-muted-foreground">Current Plan</div>
              </motion.div>
            </div>

            {/* Quick Actions */}
            <div className="p-6 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-4 font-['Plus_Jakarta_Sans']">Quick Actions</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/tools">
                  <Button variant="outline" className="w-full justify-start" data-testid="browse-tools-btn">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Browse Tools
                    <ArrowRight className="w-4 h-4 ml-auto" />
                  </Button>
                </Link>
                {user.subscription === 'free' && (
                  <Link to="/pricing">
                    <Button className="w-full justify-start glow-blue" data-testid="upgrade-btn">
                      <Crown className="w-4 h-4 mr-2" />
                      Upgrade Plan
                      <ArrowRight className="w-4 h-4 ml-auto" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Saved Tools Tab */}
          <TabsContent value="saved">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-48 rounded-xl" />
                ))}
              </div>
            ) : savedTools.length === 0 ? (
              <div className="text-center py-12">
                <Bookmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No Saved Tools</h3>
                <p className="text-muted-foreground mb-4">Save tools to access them quickly later</p>
                <Link to="/tools">
                  <Button>Browse Tools</Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="saved-tools-grid">
                {savedTools.map((tool) => (
                  <ToolCard key={tool.tool_id} tool={tool} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="max-w-2xl">
              <div className="p-6 rounded-xl border border-border bg-card mb-6">
                <h3 className="font-semibold mb-4 font-['Plus_Jakarta_Sans']">Current Plan</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold capitalize">{user.subscription} Plan</div>
                    <p className="text-sm text-muted-foreground">
                      {user.subscription === 'free' && 'Access to basic tools'}
                      {user.subscription === 'pro' && 'Access to Pro and Free tools'}
                      {user.subscription === 'business' && 'Access to all tools'}
                    </p>
                  </div>
                  <Badge className={subscriptionBadge[user.subscription]?.class}>
                    Active
                  </Badge>
                </div>
              </div>

              {user.subscription !== 'business' && (
                <div className="p-6 rounded-xl border border-primary/30 bg-primary/5">
                  <h3 className="font-semibold mb-2">Upgrade Your Plan</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get access to more powerful tools and features
                  </p>
                  <Link to="/pricing">
                    <Button className="glow-blue" data-testid="view-plans-btn">
                      View Plans
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
